<template>
  <div class="space-y-4 sm:space-y-6 pb-4 sm:pb-8 w-full">
    <!-- Unified Leave Balance, Usage & Apply Leave Action Card -->
    <div class="bg-white rounded-2xl p-3.5 sm:p-4 border border-slate-200 shadow-2xs">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <!-- Metrics: Leave Balance & Total Used -->
        <div class="grid grid-cols-2 gap-3 sm:gap-6 flex-1 divide-x divide-slate-100">
          <!-- Leave Balance -->
          <div class="flex items-center gap-3 pr-2">
            <div class="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0">
              <v-icon color="teal darken-2" size="22">mdi-wallet-travel</v-icon>
            </div>
            <div class="min-w-0">
              <span class="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-tight block truncate">
                Leave Balance
              </span>
              <div class="flex items-baseline gap-1">
                <span class="text-lg sm:text-2xl font-black text-slate-900">{{ totalLeaveBalance }}</span>
                <span class="text-[10px] sm:text-xs text-slate-400 font-semibold">days avail.</span>
              </div>
            </div>
          </div>

          <!-- Total Used -->
          <div class="flex items-center gap-3 pl-3 sm:pl-6">
            <div class="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
              <v-icon color="#1677ff" size="22">mdi-calendar-check</v-icon>
            </div>
            <div class="min-w-0">
              <span class="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-tight block truncate">
                Total Used
              </span>
              <div class="flex items-baseline gap-1">
                <span class="text-lg sm:text-2xl font-black text-slate-900">{{ totalLeaveUsed }}</span>
                <span class="text-[10px] sm:text-xs text-slate-400 font-semibold">days taken</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Apply Leave Button -->
        <div class="shrink-0 flex items-center justify-end pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
          <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
            class="white--text font-weight-bold rounded-xl h-9 text-xs px-4 text-capitalize w-full sm:w-auto shadow-xs"
            @click="leaveModalOpen = true">
            <v-icon left small color="white">mdi-calendar-plus</v-icon>
            Apply Leave
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Unified Calendar, Summary & Activity Details Card -->
    <div class="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 w-full space-y-6">
      <!-- 1. CALENDAR SECTION -->
      <div class="w-full space-y-4">
        <!-- Calendar Header Navigation -->
        <div class="flex items-center justify-between pb-2.5 border-b border-gray-100">
          <div class="flex items-center gap-1.5 sm:gap-2">
            <v-btn icon x-small sm:small class="hover:bg-gray-100" @click="prevMonth">
              <v-icon small color="grey darken-2">mdi-chevron-left</v-icon>
            </v-btn>
            <h3 class="text-sm sm:text-base font-bold text-gray-800 mb-0">
              {{ currentMonthFormatted }}
            </h3>
            <v-btn icon x-small sm:small class="hover:bg-gray-100" @click="nextMonth">
              <v-icon small color="grey darken-2">mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <div class="flex items-center justify-end ml-auto">
            <v-btn x-small text :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg font-bold text-xs" @click="goToToday">
              Today
            </v-btn>
          </div>
        </div>

        <!-- Days of Week Header -->
        <div class="grid grid-cols-7 gap-1 text-center mb-1.5">
          <span v-for="d in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']" :key="d"
            class="text-[9px] sm:text-[11px] font-bold text-gray-400 uppercase">
            {{ d }}
          </span>
        </div>

        <!-- Calendar Days Grid -->
        <div v-if="loadingAttendance" class="py-16 flex flex-col items-center justify-center">
          <v-progress-circular indeterminate color="teal" size="32"></v-progress-circular>
          <span class="text-xs text-gray-500 font-medium mt-2">Loading attendance...</span>
        </div>

        <div v-else class="grid grid-cols-7 gap-1 sm:gap-2">
          <div v-for="(day, idx) in calendarDays" :key="idx" @click="selectDay(day)" :class="[
            !day.isCurrentMonth || day.isFuture
              ? 'bg-transparent opacity-25 cursor-not-allowed pointer-events-none border-dashed border-slate-200'
              : 'cursor-pointer hover:shadow-xs',
            day.isCurrentMonth && !day.isFuture ? getDayContainerClass(day.status) : 'border-slate-100',
            day.dateString === selectedDateString ? 'ring-2 ring-[#1677ff] ring-offset-1 shadow-sm font-bold' : ''
          ]"
            class="relative min-h-[52px] sm:min-h-[64px] p-1 sm:p-1.5 rounded-xl border border-solid flex flex-col items-center justify-center transition-all select-none text-center overflow-hidden">
            <!-- Today subtle indicator dot -->
            <span v-if="day.isToday && day.isCurrentMonth"
              class="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#1677ff] ring-2 ring-white"
              title="Today"></span>

            <!-- Centered Day Number -->
            <div class="flex items-center justify-center">
              <span class="text-[12px] sm:text-sm font-black leading-none"
                :class="day.isToday ? 'text-[#1677ff] underline decoration-2 underline-offset-2' : ''">
                {{ day.dayNumber }}
              </span>
            </div>

            <!-- Attendance Status on Next Line -->
            <div v-if="day.status && !day.isFuture"
              class="mt-1 px-1 py-0.5 rounded text-[8px] sm:text-[9px] font-black uppercase tracking-wider leading-none truncate max-w-full"
              :class="getDayStatusPillClass(day.status)">
              {{ formatStatusShort(day.status) }}
            </div>
          </div>
        </div>

        <!-- Compact Color Legend defining what each color means (Scrollable single line) -->
        <div
          class="flex items-center justify-start sm:justify-center gap-x-3.5 sm:gap-x-4 pt-2 pb-0.5 border-t border-slate-100 text-[10px] sm:text-[11px] text-slate-700 font-bold select-none overflow-x-auto no-scrollbar whitespace-nowrap">
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
            <span>Present</span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0"></span>
            <span>Leave</span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
            <span>Absent</span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
            <span>Miss Punch</span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0"></span>
            <span>Week Off</span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <span class="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
            <span>Holiday</span>
          </div>
        </div>
      </div>

      <!-- 2. ATTENDANCE SUMMARY & TOTAL TRAVELLED DISTANCE (Immediately below calendar) -->
      <div class="pt-4 border-t border-slate-100">
        <div class="flex sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <span class="text-xs sm:text-sm font-extrabold text-slate-800 tracking-tight block">
            Attendance Summary
          </span>
          <div class="bg-blue-50 px-3 rounded-xl border border-blue-100/80 items-center gap-1.5">
            <v-icon x-small color="#1677ff">mdi-map-marker-distance</v-icon>
            <span class="text-[11px] font-bold text-slate-700">
              Total Travelled: <span class="font-black text-[#1677ff]">{{ totalMonthlyDistance.toFixed(1) }} km</span>
            </span>
          </div>
        </div>

        <div class="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-2.5">
          <div v-for="kpi in kpiStats" :key="kpi.label"
            class="p-2 sm:p-2.5 rounded-2xl border flex flex-col justify-between transition-all" :class="kpi.bgClass">
            <div class="flex items-center gap-1.5 mb-1.5">
              <v-icon size="16" :color="kpi.iconColor" class="shrink-0">{{ kpi.icon }}</v-icon>
              <span class="text-[10px] sm:text-[11px] font-bold text-slate-600 truncate">{{ kpi.label }}</span>
            </div>
            <div class="flex items-baseline justify-between">
              <span class="text-base sm:text-xl font-black" :class="kpi.textClass">{{ kpi.count }}</span>
              <span class="text-[9px] sm:text-[10px] text-slate-400 font-semibold">days</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. ACTIVITY DETAILS (Rendered below monthly summary in the same card) -->
      <div v-if="hasActivity" class="pt-4 border-t border-gray-100 space-y-4">
        <!-- Day Activity Header -->
        <div class="flex items-center justify-between pb-2 border-b border-gray-100">
          <div class="flex items-center gap-2">
            <div>
              <h3 class="text-sm font-extrabold text-gray-800 mb-0">
                Activity Details
              </h3>
              <p class="text-xs text-gray-500 mb-0">{{ formattedSelectedDate }}</p>
            </div>
          </div>
          <div class="flex items-center justify-end gap-2">
            <v-btn v-if="hasValidMapCoords" depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="white--text rounded-xl h-7 text-[11px] font-bold px-2.5" @click="openMapModal">
              <v-icon left x-small color="white">mdi-map-search</v-icon>
              View Map
            </v-btn>
            <span v-if="selectedDayData && selectedDayData.status"
              class="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase"
              :class="getDayStatusBadgeClass(selectedDayData.status)">
              {{ selectedDayData.status }}
            </span>
          </div>
        </div>

        <div class="space-y-3">
          <!-- Punch Timings & Hours -->
          <div v-if="selectedDayData && (selectedDayData.checkIn !== '-' || selectedDayData.checkOut !== '-')"
            class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">First Clock In</span>
              <span class="text-sm font-extrabold text-gray-800 font-mono">{{ selectedDayData.checkIn || '-' }}</span>
            </div>
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">Last Clock Out</span>
              <span class="text-sm font-extrabold text-gray-800 font-mono">{{ selectedDayData.checkOut || '-' }}</span>
            </div>
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">Total Hours</span>
              <span class="text-sm font-extrabold text-teal-700 font-mono">{{ selectedDayData.workingHours || '-'
                }}</span>
            </div>
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">Distance Travelled</span>
              <span class="text-sm font-extrabold text-indigo-700 font-mono">{{ selectedDayData.distance || '0.0 km'
                }}</span>
            </div>
          </div>

          <div
            v-else-if="selectedDayData && (selectedDayData.workingHours !== '-' || selectedDayData.distance !== '0.0 km')"
            class="grid grid-cols-2 gap-2 text-xs">
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">Total Hours</span>
              <span class="text-sm font-extrabold text-teal-700 font-mono">{{ selectedDayData.workingHours || '-'
                }}</span>
            </div>
            <div class="p-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <span class="text-gray-400 text-[10px] uppercase font-bold block">Distance Travelled</span>
              <span class="text-sm font-extrabold text-indigo-700 font-mono">{{ selectedDayData.distance || '0.0 km'
                }}</span>
            </div>
          </div>

          <!-- Detailed Timeline Logs -->
          <div v-if="selectedDayTimeline.length > 0" class="pt-1">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-xs font-bold text-gray-700 mb-0">Punch & Location Logs</h4>
              <span class="text-[10px] text-gray-400 font-medium">{{ selectedDayTimeline.length }} entries</span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
              <div v-for="(log, idx) in selectedDayTimeline" :key="idx"
                class="p-2.5 bg-gray-50/70 rounded-xl border border-gray-100 flex items-start gap-2 text-xs hover:bg-teal-50/40 transition-colors"
                @click="openMapModal(log)">
                <v-icon small :color="log.type === 'CHECKED_IN' ? 'teal' : 'red'">
                  {{ log.type === 'CHECKED_IN' ? 'mdi-login' : 'mdi-logout' }}
                </v-icon>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between">
                    <span class="font-bold text-gray-800">{{ log.title }}</span>
                    <span class="font-mono text-[11px] text-gray-500">{{ log.time }}</span>
                  </div>
                  <span v-if="log.location" class="text-[11px] text-gray-400 truncate block mt-0.5">{{ log.location
                    }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 90% Cover Map Dialog -->
    <v-dialog v-model="mapModalOpen" max-width="90vw" width="90vw">
      <v-card class="rounded-2xl overflow-hidden flex flex-col" style="height: 85vh;">
        <!-- Dialog Header -->
        <div class="px-5 py-3.5 border-b border-gray-100 bg-white shrink-0">
          <div class="flex items-center justify-between gap-2.5">
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0">
                <v-icon small color="indigo darken-2">mdi-map-marker-path</v-icon>
              </div>
              <div>
                <h3 class="text-sm sm:text-base font-bold text-gray-900 mb-0">
                  Daily Travel Route
                </h3>
                <p class="text-xs text-gray-500 mb-0">
                  {{ formattedSelectedDate }} &bull;
                  <span class="font-bold text-gray-700">{{ selectedDayData?.distance || '0.0 km' }}</span> travelled
                </p>
              </div>
            </div>
            <button type="button"
              class="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 border-none bg-transparent cursor-pointer transition-colors"
              @click="mapModalOpen = false">
              <v-icon color="grey darken-2">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Dialog Map Container Body -->
        <div class="relative flex-1 w-full h-full bg-gray-100">
          <div id="attendance-activity-map" ref="activityMapRef" class="w-full h-full z-0"></div>
          <!-- Loading overlay for map data -->
          <div v-if="loadingDayLocation"
            class="absolute inset-0 bg-white/70 backdrop-blur-2xs flex flex-col items-center justify-center z-10">
            <v-progress-circular indeterminate color="teal" size="32"></v-progress-circular>
            <span class="text-xs font-bold text-gray-600 mt-2">Loading route map...</span>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Apply Leave Modal (Full App Feature Parity) -->
    <v-dialog v-model="leaveModalOpen" max-width="540" persistent>
      <v-card class="rounded-2xl pa-5">
        <div class="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 rounded-lg bg-teal-50 text-teal-700">
              <v-icon small color="teal darken-2">mdi-calendar-check</v-icon>
            </div>
            <div>
              <h3 class="text-base font-bold text-gray-900 mb-0">Apply For Leave</h3>
              <p class="text-[11px] text-gray-500 mb-0">Submit leave request for approval</p>
            </div>
          </div>
          <button type="button" class="text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer p-1"
            @click="leaveModalOpen = false">
            <v-icon small color="grey">mdi-close</v-icon>
          </button>
        </div>

        <v-form ref="leaveForm" lazy-validation class="space-y-4">
          <!-- Step 1: Approvers Selection (from App) -->
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">
              Approvers <span class="text-rose-500">*</span>
            </label>
            <v-select v-model="leaveForm.approvers" :items="approverSelectOptions" item-text="text" item-value="value"
              multiple chips small-chips deletable-chips placeholder="Select one or more approvers" outlined dense
              :loading="loadingApprovers" class="rounded-xl text-xs" prepend-inner-icon="mdi-account-multiple-outline"
              hide-details="auto">
              <template v-slot:no-data>
                <div class="pa-2 text-xs text-gray-400">No active employees found</div>
              </template>
            </v-select>
          </div>

          <!-- Step 2: Leave Type Selection -->
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">
              Leave Type <span class="text-rose-500">*</span>
            </label>
            <v-select v-model="leaveForm.type" :items="leaveTypeDisplayOptions" item-text="text" item-value="value"
              outlined dense class="rounded-xl text-xs" prepend-inner-icon="mdi-tag-outline" hide-details="auto">
            </v-select>
          </div>

          <!-- Step 3: Select Dates (Calendar Multi-date / Duration) -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-bold text-gray-700">
                Select Dates <span class="text-rose-500">*</span>
              </label>
              <span v-if="leaveDurationDays > 0" class="text-xs font-extrabold text-teal-700">
                {{ leaveDurationDays }} day{{ leaveDurationDays > 1 ? 's' : '' }} selected
              </span>
            </div>

            <v-menu v-model="leaveDatePickerMenu" :close-on-content-click="false" transition="scale-transition" offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="leaveForm.dates.join(', ')" placeholder="Choose leave dates from calendar"
                  prepend-inner-icon="mdi-calendar-month-outline" readonly outlined dense v-bind="attrs" v-on="on"
                  class="rounded-xl text-xs" hide-details="auto"></v-text-field>
              </template>
              <v-card class="rounded-xl shadow-lg pa-2">
                <v-date-picker v-model="leaveForm.dates" multiple no-title :color="$vuetify.theme.currentTheme.employeePrimary"
                  class="rounded-lg"></v-date-picker>
                <div class="flex justify-end p-2 border-t border-gray-100">
                  <v-btn small depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text font-bold"
                    @click="leaveDatePickerMenu = false">
                    Done
                  </v-btn>
                </div>
              </v-card>
            </v-menu>

            <!-- Selected Date Chips Quick Review -->
            <div v-if="leaveForm.dates && leaveForm.dates.length > 0" class="flex flex-wrap gap-1 mt-2">
              <span v-for="(dateStr, dIdx) in leaveForm.dates" :key="dIdx"
                class="inline-flex items-center gap-1 text-[11px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-md">
                {{ dateStr }}
                <v-icon x-small color="teal darken-2" class="cursor-pointer"
                  @click="leaveForm.dates.splice(dIdx, 1)">mdi-close</v-icon>
              </span>
            </div>
          </div>

          <!-- Step 4: Comments / Reason -->
          <div>
            <label class="block text-xs font-bold text-gray-700 mb-1">
              Comments / Reason <span class="text-rose-500">*</span>
            </label>
            <v-textarea v-model="leaveForm.reason" rows="3" outlined dense class="rounded-xl text-xs"
              placeholder="Enter details or comments for the approver..." hide-details="auto"></v-textarea>
          </div>
        </v-form>

        <v-card-actions class="justify-end gap-2 mt-5 px-0 pb-0 border-t border-gray-100 pt-4">
          <v-btn text small class="rounded-xl text-xs font-bold" @click="leaveModalOpen = false">Cancel</v-btn>
          <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl text-xs font-bold px-5"
            :loading="submittingLeave" @click="submitLeaveApplication">
            <template v-if="leaveDurationDays > 0">
              Apply Leave For {{ leaveDurationDays }} Day{{ leaveDurationDays > 1 ? 's' : '' }}
            </template>
            <template v-else>
              Apply Leave
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import {
  getAttendanceDataV2,
  getLocationOverview,
  getUserLeaveQuota,
  addNewLeaveRequest,
  getUsersList,
  getUserData
} from '@/app/services/dashboard'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export default {
  name: 'AppAttendance',
  data() {
    return {
      currentMonth: dayjs(),
      selectedDateString: dayjs().format('YYYY-MM-DD'),
      loadingAttendance: false,
      attendanceList: [],
      dailyDistances: {},
      totalMonthlyDistance: 0,
      mapModalOpen: false,
      leaveModalOpen: false,
      startDateMenu: false,
      endDateMenu: false,
      submittingLeave: false,
      leaveQuotas: [],
      leaveForm: {
        type: 'CASUAL_LEAVE',
        dates: [dayjs().format('YYYY-MM-DD')],
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        approvers: [],
        reason: ''
      },
      approverList: [],
      loadingApprovers: false,
      leaveDatePickerMenu: false,
      leaveTypeOptions: [
        { text: 'Casual Leave (CL)', value: 'CASUAL_LEAVE', key: 'CASUAL_LEAVE' },
        { text: 'Sick Leave (SL)', value: 'SICK_LEAVE', key: 'SICK_LEAVE' },
        { text: 'Privilege / Earned Leave (PL)', value: 'EARNED_LEAVE', key: 'EARNED_LEAVE' }
      ],
      mapInstance: null,
      mapMarkers: [],
      directionsService: null,
      directionsRenderer: null,
      polylinePath: null,
      loadingDayLocation: false,
      hasValidMapCoords: false,
      dayLocationData: [],
      currentDayEntry: null
    }
  },
  computed: {
    totalLeaveBalance() {
      if (!this.leaveQuotas || !this.leaveQuotas.length) return 0
      return this.leaveQuotas.reduce((acc, q) => acc + (q.available || 0), 0)
    },
    totalLeaveUsed() {
      if (!this.leaveQuotas || !this.leaveQuotas.length) return 0
      return this.leaveQuotas.reduce((acc, q) => acc + (q.used || 0), 0)
    },
    leaveTypeDisplayOptions() {
      return this.leaveTypeOptions.map((opt) => {
        const quota = this.leaveQuotas.find((q) => {
          const title = (q.title || '').toUpperCase()
          if (opt.value === 'CASUAL_LEAVE' && (title.includes('CASUAL') || title.includes('CL'))) return true
          if (opt.value === 'SICK_LEAVE' && (title.includes('SICK') || title.includes('SL'))) return true
          if (opt.value === 'EARNED_LEAVE' && (title.includes('EARNED') || title.includes('PRIVILEGE') || title.includes('PL') || title.includes('EL'))) return true
          return false
        })
        const avail = quota ? quota.available : null
        return {
          text: avail !== null ? `${opt.text} (${avail} avail.)` : opt.text,
          value: opt.value
        }
      })
    },
    leaveDurationDays() {
      if (Array.isArray(this.leaveForm.dates) && this.leaveForm.dates.length > 0) {
        return this.leaveForm.dates.length
      }
      return 0
    },
    approverSelectOptions() {
      return this.approverList.map((u) => ({
        text: `${u.firstName || ''} ${u.lastName || ''}`.trim() || `${u.systemUserId?.firstName || ''} ${u.systemUserId?.lastName || ''}`.trim() || u.systemUserId.email || u.systemUserId.mobile || 'Colleague',
        value: u._id
      }))
    },
    hasActivity() {
      if (this.hasValidMapCoords || this.loadingDayLocation) return true
      if (Array.isArray(this.dayLocationData) && this.dayLocationData.length > 0) return true
      if (this.selectedDayTimeline && this.selectedDayTimeline.length > 0) return true
      if (this.selectedDayData) {
        const hasPunch = this.selectedDayData.checkIn !== '-' || this.selectedDayData.checkOut !== '-'
        const hasHours = this.selectedDayData.workingHours !== '-'
        const hasDist = this.selectedDayData.distance && this.selectedDayData.distance !== '0.0 km'
        return !!(hasPunch || hasHours || hasDist)
      }
      return false
    },
    currentMonthFormatted() {
      return this.currentMonth.format('MMMM YYYY')
    },
    formattedSelectedDate() {
      return dayjs(this.selectedDateString).format('dddd, DD MMMM YYYY')
    },
    kpiStats() {
      let present = 0
      let leaves = 0
      let absent = 0
      let missPunch = 0
      let weekOff = 0
      let holidays = 0

      this.attendanceList.forEach((a) => {
        const s = (a.attendanceStatus || '').toUpperCase()
        if (s === 'PRESENT') present++
        else if (s === 'LEAVE') leaves++
        else if (s === 'ABSENT') absent++
        else if (s === 'MISS_PUNCH') missPunch++
        else if (s === 'WEEK_OFF') weekOff++
        else if (s === 'HOLIDAY') holidays++
      })

      return [
        {
          label: 'Present',
          count: present,
          icon: 'mdi-check-circle-outline',
          iconColor: '#16a34a',
          dotClass: 'bg-emerald-500',
          textClass: 'text-emerald-700',
          bgClass: 'bg-emerald-50/60 border-emerald-100'
        },
        {
          label: 'Leaves',
          count: leaves,
          icon: 'mdi-calendar-account-outline',
          iconColor: '#0284c7',
          dotClass: 'bg-sky-500',
          textClass: 'text-sky-700',
          bgClass: 'bg-sky-50/60 border-sky-100'
        },
        {
          label: 'Absent',
          count: absent,
          icon: 'mdi-close-circle-outline',
          iconColor: '#dc2626',
          dotClass: 'bg-rose-500',
          textClass: 'text-rose-700',
          bgClass: 'bg-rose-50/60 border-rose-100'
        },
        {
          label: 'Miss Punch',
          count: missPunch,
          icon: 'mdi-clock-alert-outline',
          iconColor: '#d97706',
          dotClass: 'bg-amber-500',
          textClass: 'text-amber-700',
          bgClass: 'bg-amber-50/60 border-amber-100'
        },
        {
          label: 'Weekly Off',
          count: weekOff,
          icon: 'mdi-coffee-outline',
          iconColor: '#64748b',
          dotClass: 'bg-slate-400',
          textClass: 'text-slate-700',
          bgClass: 'bg-slate-50 border-slate-200/60'
        },
        {
          label: 'Holidays',
          count: holidays,
          icon: 'mdi-party-popper',
          iconColor: '#9333ea',
          dotClass: 'bg-purple-500',
          textClass: 'text-purple-700',
          bgClass: 'bg-purple-50/60 border-purple-100'
        }
      ]
    },
    calendarDays() {
      const startOfMonth = this.currentMonth.startOf('month')
      const endOfMonth = this.currentMonth.endOf('month')
      const startDayOfWeek = startOfMonth.day() // 0 is Sunday
      const daysInMonth = this.currentMonth.daysInMonth()
      const days = []

      // Previous month days for padding
      for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const d = startOfMonth.subtract(i + 1, 'day')
        days.push({
          dayNumber: d.date(),
          dateString: d.format('YYYY-MM-DD'),
          isCurrentMonth: false,
          isToday: false,
          status: null,
          workingHours: null,
          distance: null
        })
      }

      // Current month days
      const today = dayjs().startOf('day')
      const todayStr = today.format('YYYY-MM-DD')
      for (let i = 1; i <= daysInMonth; i++) {
        const d = startOfMonth.date(i)
        const dateStr = d.format('YYYY-MM-DD')
        const isFuture = d.isAfter(today, 'day')
        const att = this.attendanceList.find((a) => dayjs(a.attendanceDate).format('YYYY-MM-DD') === dateStr)
        const dist = this.dailyDistances[dateStr]

        days.push({
          dayNumber: i,
          dateString: dateStr,
          isCurrentMonth: true,
          isToday: dateStr === todayStr,
          isFuture,
          status: att?.attendanceStatus || null,
          workingHours: att?.actualWorkingHrs || null,
          distance: dist ? `${dist.toFixed(1)} km` : null,
          rawAttendance: att
        })
      }

      // Next month padding days to complete grid
      const remaining = 35 - days.length > 0 ? 35 - days.length : (42 - days.length > 0 ? 42 - days.length : 0)
      for (let i = 1; i <= remaining; i++) {
        const d = endOfMonth.add(i, 'day')
        days.push({
          dayNumber: d.date(),
          dateString: d.format('YYYY-MM-DD'),
          isCurrentMonth: false,
          isToday: false,
          isFuture: true,
          status: null,
          workingHours: null,
          distance: null
        })
      }

      return days
    },
    selectedDayData() {
      const att = this.attendanceList.find(
        (a) => dayjs(a.attendanceDate).format('YYYY-MM-DD') === this.selectedDateString
      )
      if (!att) return null

      const checkInEntry = att.checkInOutData?.find((c) => c.status === 'CHECKED_IN')
      const checkOutEntry = att.checkInOutData?.find((c) => c.status === 'CHECKED_OUT')
      const dist = this.dailyDistances[this.selectedDateString]

      return {
        status: att.attendanceStatus,
        checkIn: checkInEntry ? dayjs(checkInEntry.time).format('HH:mm') : '-',
        checkOut: checkOutEntry ? dayjs(checkOutEntry.time).format('HH:mm') : '-',
        workingHours: att.actualWorkingHrs || '-',
        distance: dist ? `${dist.toFixed(1)} km` : '0.0 km',
        checkInOutData: att.checkInOutData || []
      }
    },
    selectedDayTimeline() {
      const logs = []
      if (Array.isArray(this.dayLocationData) && this.dayLocationData.length > 0) {
        this.dayLocationData.forEach((item) => {
          logs.push({
            type: item.activity?.action === 'checkOut' ? 'CHECKED_OUT' : 'CHECKED_IN',
            title: this.formatActivityAction(item.activity?.action) || item.activity?.activityType || 'Activity',
            time: item.activity?.time ? dayjs(item.activity.time).format('hh:mm:ss A') : '',
            location: item.location?.formatted_address || item.location?.address || '',
            lat: item.location?.geometry?.lat || item.location?.geometry?.latitude || item.location?.latitude,
            lng: item.location?.geometry?.lng || item.location?.geometry?.longitude || item.location?.longitude
          })
        })
        return logs
      }

      if (this.selectedDayData?.checkInOutData) {
        return this.selectedDayData.checkInOutData.map((item) => ({
          type: item.status,
          title: item.status === 'CHECKED_IN' ? 'Clock In' : 'Clock Out',
          time: dayjs(item.time).format('hh:mm:ss A'),
          location: item.location?.address || '',
          lat: item.location?.latitude || item.latitude,
          lng: item.location?.longitude || item.longitude
        }))
      }
      return []
    }
  },
  watch: {
    selectedDateString: {
      immediate: true,
      handler(newDate) {
        if (newDate) {
          this.fetchDayLocationAndDrawMap()
        }
      }
    },
    mapModalOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.mapInstance && window.google) {
              window.google.maps.event.trigger(this.mapInstance, 'resize')
            }
          }, 200)
        })
      } else {
        this.clearMapDrawings()
        this.mapInstance = null
      }
    }
  },
  mounted() {
    this.fetchMonthlyAttendance()
    this.fetchLeaveQuota()
    this.fetchApprovers()
    this.ensureGoogleMapsLoaded()
  },
  methods: {
    formatActivityAction(action) {
      switch (action) {
        case 'checkIn':
          return 'Clock In'
        case 'checkOut':
          return 'Clock Out'
        case 'taskStart':
          return 'Visit Start'
        case 'taskEnded':
          return 'Visit Ended'
        case 'taskComplete':
          return 'Visit Complete'
        case 'travel':
          return 'Travelling'
        case 'uploadSelfie':
          return 'Upload Selfie'
        case 'questionnaire':
          return 'Questionnaire'
        case 'addMeetingNotes':
          return 'Meeting Notes'
        default:
          return action || 'Activity'
      }
    },
    ensureGoogleMapsLoaded() {
      if (window.google && window.google.maps) {
        return Promise.resolve()
      }
      return new Promise((resolve) => {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
        if (existingScript) {
          const checkTimer = setInterval(() => {
            if (window.google && window.google.maps) {
              clearInterval(checkTimer)
              resolve()
            }
          }, 200)
          return
        }

        const script = document.createElement('script')
        const apiKey = process.env.VUE_APP_MAP_KEY || ''
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=Function.prototype`
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = (err) => console.error('Error loading Google Maps API:', err)
        document.head.appendChild(script)
      })
    },
    async fetchDayLocationAndDrawMap() {
      try {
        this.loadingDayLocation = true
        this.hasValidMapCoords = false
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const date = this.selectedDateString

        const res = await getLocationOverview({ userId, date, start: date, end: date })
        const rawData = res.data?.locationHistory || res.data?.data || res.data || []

        let dayEntry = null
        if (Array.isArray(rawData)) {
          dayEntry = rawData.find((d) => {
            const dDate = d.travelDate || d.date || d.attendanceDate || d.createdAt
            return dDate && dayjs(dDate).format('YYYY-MM-DD') === date
          }) || rawData[0]
        } else {
          dayEntry = rawData
        }

        const locationDataList = dayEntry?.locationData || []
        this.dayLocationData = locationDataList
        this.currentDayEntry = dayEntry

        // Check if there are valid coordinates in dayEntry or timeline
        this.checkMapCoordinatesAvailable(dayEntry)

        // Update daily distance if available in dayEntry
        const distFromPayload = dayEntry?.dailyDistanceTraveled?.distance
        if (distFromPayload !== undefined && distFromPayload !== null) {
          this.$set(this.dailyDistances, date, distFromPayload)
        }
      } catch (err) {
        console.error('Error fetching day location data:', err)
        this.renderFallbackMap()
      } finally {
        this.loadingDayLocation = false
      }
    },
    checkMapCoordinatesAvailable(dayEntry) {
      if (Array.isArray(this.dayLocationData) && this.dayLocationData.length > 0) {
        this.hasValidMapCoords = true
        return
      }
      if (dayEntry?.dailyDistanceTraveled?.pairDistance && dayEntry.dailyDistanceTraveled.pairDistance.length > 0) {
        this.hasValidMapCoords = true
        return
      }
      if (this.selectedDayData?.checkInOutData && this.selectedDayData.checkInOutData.length > 0) {
        this.hasValidMapCoords = true
        return
      }
      this.hasValidMapCoords = false
    },
    async openMapModal(focusLog = null) {
      this.mapModalOpen = true
      await this.ensureGoogleMapsLoaded()
      await this.$nextTick()
      setTimeout(() => {
        this.renderMapWithRoute(this.currentDayEntry)
        if (focusLog) {
          this.panToLocation(focusLog)
        }
      }, 250)
    },
    renderMapWithRoute(dayEntry) {
      if (!window.google || !window.google.maps) return

      const mapEl = document.getElementById('attendance-activity-map')
      if (!mapEl) return

      const points = []

      // 1. Extract from dayLocationData
      if (Array.isArray(this.dayLocationData) && this.dayLocationData.length > 0) {
        this.dayLocationData.forEach((item) => {
          const geom = item.location?.geometry || item.geometry
          const lat = geom?.lat || geom?.latitude || item.location?.latitude || item.latitude
          const lng = geom?.lng || geom?.longitude || item.location?.longitude || item.longitude
          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            points.push({
              lat: Number(lat),
              lng: Number(lng),
              title: this.formatActivityAction(item.activity?.action) || item.activity?.activityType || 'Activity',
              time: item.activity?.time ? dayjs(item.activity.time).format('hh:mm A') : '',
              address: item.location?.formatted_address || item.location?.address || ''
            })
          }
        })
      }

      // 2. Extract from pairDistance if locationData didn't have points
      if (points.length === 0 && dayEntry?.dailyDistanceTraveled?.pairDistance) {
        dayEntry.dailyDistanceTraveled.pairDistance.forEach((pair, idx) => {
          const origGeom = pair.origin_details?.geometry
          const destGeom = pair.destination_details?.geometry
          if (idx === 0 && origGeom?.latitude && origGeom?.longitude) {
            points.push({
              lat: Number(origGeom.latitude),
              lng: Number(origGeom.longitude),
              title: 'Start Location',
              time: '',
              address: pair.origin_details?.formatted_address || pair.origin || ''
            })
          }
          if (destGeom?.latitude && destGeom?.longitude) {
            points.push({
              lat: Number(destGeom.latitude),
              lng: Number(destGeom.longitude),
              title: `Stop ${idx + 1}`,
              time: '',
              address: pair.destination_details?.formatted_address || pair.destination || ''
            })
          }
        })
      }

      // 3. Extract from selectedDayData checkInOutData if still empty
      if (points.length === 0 && this.selectedDayData?.checkInOutData) {
        this.selectedDayData.checkInOutData.forEach((item) => {
          const geom = item.location?.geometry || item.geometry
          const lat = geom?.lat || geom?.latitude || item.location?.latitude || item.latitude
          const lng = geom?.lng || geom?.longitude || item.location?.longitude || item.longitude
          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
            points.push({
              lat: Number(lat),
              lng: Number(lng),
              title: item.status === 'CHECKED_IN' ? 'Clock In' : 'Clock Out',
              time: dayjs(item.time).format('hh:mm A'),
              address: item.location?.address || ''
            })
          }
        })
      }

      if (points.length === 0) {
        this.hasValidMapCoords = false
        return
      }

      this.hasValidMapCoords = true

      // Initialize Google Map
      const center = points[0]
      if (!this.mapInstance) {
        this.mapInstance = new window.google.maps.Map(mapEl, {
          center: { lat: center.lat, lng: center.lng },
          zoom: 14,
          mapTypeId: 'roadmap',
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy'
        })
      } else {
        this.mapInstance.setCenter({ lat: center.lat, lng: center.lng })
      }

      // Clear existing markers & polyline
      this.clearMapDrawings()

      const bounds = new window.google.maps.LatLngBounds()
      const pathCoordinates = []

      // Create Custom Markers
      points.forEach((p, index) => {
        const latLng = new window.google.maps.LatLng(p.lat, p.lng)
        bounds.extend(latLng)
        pathCoordinates.push(latLng)

        const isStart = index === 0
        const isEnd = index === points.length - 1 && points.length > 1
        const pinColor = isStart ? '#059669' : (isEnd ? '#dc2626' : '#4f46e5')

        const marker = new window.google.maps.Marker({
          position: latLng,
          map: this.mapInstance,
          title: `${p.title} (${p.time})`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: pinColor,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2
          }
        })

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="font-family: sans-serif; font-size: 11px; padding: 2px;">
              <strong style="color: #1e293b;">${p.title}</strong>
              <div style="color: #64748b; font-size: 10px;">${p.time}</div>
              ${p.address ? `<div style="color: #334155; margin-top: 2px;">${p.address}</div>` : ''}
            </div>
          `
        })

        marker.addListener('click', () => {
          infoWindow.open(this.mapInstance, marker)
        })

        this.mapMarkers.push(marker)
      })

      // Draw Journey Route Polyline
      if (pathCoordinates.length > 1) {
        this.polylinePath = new window.google.maps.Polyline({
          path: pathCoordinates,
          geodesic: true,
          strokeColor: '#4f46e5',
          strokeOpacity: 0.85,
          strokeWeight: 4
        })
        this.polylinePath.setMap(this.mapInstance)
      }

      if (points.length > 1) {
        this.mapInstance.fitBounds(bounds)
      } else {
        this.mapInstance.setZoom(15)
      }
    },
    clearMapDrawings() {
      if (this.mapMarkers) {
        this.mapMarkers.forEach((m) => m.setMap(null))
      }
      this.mapMarkers = []
      if (this.polylinePath) {
        this.polylinePath.setMap(null)
        this.polylinePath = null
      }
    },
    panToLocation(log) {
      if (!this.mapInstance || !window.google) return
      const geom = log.location?.geometry || log.geometry
      const lat = geom?.lat || log.latitude || log.lat
      const lng = geom?.lng || log.longitude || log.lng
      if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
        this.mapInstance.panTo({ lat: Number(lat), lng: Number(lng) })
        this.mapInstance.setZoom(16)
      }
    },
    renderFallbackMap() {
      this.hasValidMapCoords = false
    },
    prevMonth() {
      this.currentMonth = this.currentMonth.subtract(1, 'month')
      this.fetchMonthlyAttendance()
    },
    nextMonth() {
      this.currentMonth = this.currentMonth.add(1, 'month')
      this.fetchMonthlyAttendance()
    },
    goToToday() {
      this.currentMonth = dayjs()
      this.selectedDateString = dayjs().format('YYYY-MM-DD')
      this.fetchMonthlyAttendance()
    },
    async fetchMonthlyAttendance() {
      try {
        this.loadingAttendance = true
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const start = this.currentMonth.startOf('month').format('YYYY-MM-DD')
        const end = this.currentMonth.endOf('month').format('YYYY-MM-DD')

        const [attRes, locRes] = await Promise.allSettled([
          getAttendanceDataV2({ userId, start, end }),
          getLocationOverview({ userId, start, end })
        ])

        if (attRes.status === 'fulfilled') {
          const list = attRes.value.data?.employeeAttendance || attRes.value.data?.data || []
          this.attendanceList = Array.isArray(list) ? list : []
        }

        if (locRes.status === 'fulfilled') {
          const locs = locRes.value.data?.locationHistory || locRes.value.data?.data || locRes.value.data || []
          let totalDist = 0
          const distMap = {}
          if (Array.isArray(locs)) {
            locs.forEach((item) => {
              const d = dayjs(item.travelDate || item.date || item.createdAt).format('YYYY-MM-DD')
              const dist = item.dailyDistanceTraveled?.distance || 0
              distMap[d] = dist
              totalDist += dist
            })
          }
          this.dailyDistances = distMap
          this.totalMonthlyDistance = totalDist
        }
      } catch (err) {
        console.error('Error loading attendance:', err)
      } finally {
        this.loadingAttendance = false
      }
    },
    async fetchLeaveQuota() {
      try {
        const res = await getUserLeaveQuota()
        const quota = res.data?.data || res.data?.quota || res.data
        if (quota?.commonLeaves) {
          this.leaveQuotas = quota.commonLeaves.map((l) => ({
            title: l.typeOfLeave?.replace(/_/g, ' ') || 'Leave',
            total: l.numberOfDays || 0,
            used: l.usedBalance || 0,
            available: (l.numberOfDays || 0) - (l.usedBalance || 0)
          }))
        } else {
          this.leaveQuotas = [
            { title: 'Casual Leave (CL)', total: 12, used: 2, available: 10 },
            { title: 'Sick Leave (SL)', total: 10, used: 1, available: 9 },
            { title: 'Earned Leave (EL)', total: 15, used: 0, available: 15 }
          ]
        }
      } catch (err) {
        console.error('Error fetching leave quota:', err)
      }
    },
    async fetchApprovers() {
      try {
        this.loadingApprovers = true
        const [usersRes, userProfileRes] = await Promise.allSettled([
          getUsersList({ status: 'ACTIVE', page: 1, limit: 100 }),
          getUserData()
        ])

        if (usersRes.status === 'fulfilled') {
          const list = usersRes.value.data?.data || usersRes.value.data?.users || usersRes.value.data || []
          this.approverList = Array.isArray(list) ? list : []
        }

        if (userProfileRes.status === 'fulfilled') {
          const userObj = userProfileRes.value.data?.data || userProfileRes.value.data
          const reportsTo = userObj?.reportesTo || userObj?.reportsTo
          if (reportsTo && reportsTo._id) {
            this.leaveForm.approvers = [reportsTo._id]
          }
        }
      } catch (err) {
        console.warn('Error fetching approvers:', err)
      } finally {
        this.loadingApprovers = false
      }
    },
    async submitLeaveApplication() {
      if (!this.leaveForm.reason) {
        this.$toast?.error?.('Please enter a reason/comment for leave')
        return
      }
      if (!this.leaveForm.dates || !this.leaveForm.dates.length) {
        this.$toast?.error?.('Please select at least one leave date')
        return
      }
      try {
        this.submittingLeave = true
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const sortedDates = [...this.leaveForm.dates].sort()
        const duration = `${sortedDates.length}d`
        const payload = {
          typeOfLeaveApplied: this.leaveForm.type,
          leaveDates: sortedDates,
          duration,
          requestApprovalsfrom: (this.leaveForm.approvers || []).map((apprId) => ({ approvalFrom: apprId })),
          comments: [
            {
              comment: this.leaveForm.reason,
              commentBy: userId,
              commentOn: dayjs().format('YYYY-MM-DD'),
              commentType: 'NEW_REQUEST'
            }
          ],
          userId
        }
        await addNewLeaveRequest(payload)
        this.$toast?.success?.(`Leave request for ${sortedDates.length} day(s) submitted successfully!`)
        this.leaveModalOpen = false
        this.fetchLeaveQuota()
      } catch (err) {
        console.error('Error submitting leave:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to submit leave request')
      } finally {
        this.submittingLeave = false
      }
    },
    formatStatusShort(status) {
      if (!status) return ''
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'P'
      if (s === 'LEAVE' || s === 'LEAVES') return 'L'
      if (s === 'ABSENT') return 'A'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'MP'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'OFF'
      if (s === 'HOLIDAY' || s === 'HOLIDAYS') return 'H'
      return s.slice(0, 3)
    },
    selectDay(day) {
      if (!day || !day.isCurrentMonth || day.isFuture) return
      this.selectedDateString = day.dateString
    },
    getDayContainerClass(status) {
      if (!status) return 'bg-white hover:bg-slate-50 border-slate-200/70 text-slate-700'
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'bg-[#f0fdf4] hover:bg-[#dcfce7]/60 border-emerald-200/70 text-emerald-900'
      if (s === 'LEAVE' || s === 'LEAVES') return 'bg-[#f0f9ff] hover:bg-[#e0f2fe]/60 border-sky-200/70 text-sky-900'
      if (s === 'ABSENT') return 'bg-[#fff1f2] hover:bg-[#ffe4e6]/60 border-rose-200/70 text-rose-900'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'bg-[#fffbeb] hover:bg-[#fef3c7]/60 border-amber-200/70 text-amber-900'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'bg-slate-50/80 hover:bg-slate-100/70 border-slate-200/70 text-slate-700'
      if (s === 'HOLIDAY' || s === 'HOLIDAYS') return 'bg-[#faf5ff] hover:bg-[#f3e8ff]/60 border-purple-200/70 text-purple-900'
      return 'bg-white hover:bg-slate-50 border-slate-200/70 text-slate-700'
    },
    getDayStatusPillClass(status) {
      if (!status) return 'bg-slate-100 text-slate-600'
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'bg-emerald-100 text-emerald-800'
      if (s === 'LEAVE' || s === 'LEAVES') return 'bg-sky-100 text-sky-800'
      if (s === 'ABSENT') return 'bg-rose-100 text-rose-800'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'bg-amber-100 text-amber-800'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'bg-slate-100 text-slate-600'
      if (s === 'HOLIDAY' || s === 'HOLIDAYS') return 'bg-purple-100 text-purple-800'
      return 'bg-slate-100 text-slate-600'
    },
    getDayStatusDotClass(status) {
      if (!status) return 'bg-gray-400'
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'bg-emerald-500'
      if (s === 'LEAVE' || s === 'LEAVES') return 'bg-blue-500'
      if (s === 'ABSENT') return 'bg-rose-500'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'bg-amber-500'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'bg-gray-400'
      return 'bg-purple-500'
    },
    getDayStatusTextClass(status) {
      if (!status) return 'text-gray-500'
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'text-emerald-700'
      if (s === 'LEAVE' || s === 'LEAVES') return 'text-blue-700'
      if (s === 'ABSENT') return 'text-rose-700'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'text-amber-700'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'text-gray-500'
      return 'text-purple-700'
    },
    getDayStatusBadgeClass(status) {
      if (!status) return 'bg-gray-100 text-gray-800'
      const s = status.toUpperCase()
      if (s === 'PRESENT') return 'bg-emerald-100 text-emerald-800'
      if (s === 'LEAVE' || s === 'LEAVES') return 'bg-blue-100 text-blue-800'
      if (s === 'ABSENT') return 'bg-rose-100 text-rose-800'
      if (s === 'MISS_PUNCH' || s === 'MISSED_PUNCH') return 'bg-amber-100 text-amber-800'
      if (s === 'WEEK_OFF' || s === 'WEEKLY_OFF') return 'bg-gray-100 text-gray-800'
      return 'bg-purple-100 text-purple-800'
    }
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
